import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtMoney } from './ProcurementShared';

export default function ProcurementSuppliers() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/procurement/suppliers?per_page=50`;
      if (status) url += `&status=${status}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status, search]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const viewSupplier = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/procurement/suppliers/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  const removeItem = async (id) => {
    if (!window.confirm('Delete this supplier?')) return;
    const res = await fetch(`${API}/companies/${companyId}/procurement/suppliers/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Cannot delete'); return; }
    fetchList();
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🏢 Suppliers</h2>
        <button style={btnPrimary} onClick={() => setEditing({})}>+ New Supplier</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inputStyle, width: 220 }} placeholder="Search name/code/email/tax#..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={{ ...inputStyle, width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="incomplete">Incomplete</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Code', 'Name', 'Contact', 'Phone', 'Total Purchased', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No suppliers</td></tr>}
              {list.map(s => (
                <tr key={s.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{s.supplier_code}</td>
                  <td style={tdStyle}><a href="#view" onClick={e => { e.preventDefault(); viewSupplier(s.id); }} style={{ color: '#3b82f6' }}>{s.name}</a></td>
                  <td style={tdStyle}>{s.contact_person || '—'}</td>
                  <td style={tdStyle}>{s.phone || '—'}</td>
                  <td style={tdStyle}>{fmtMoney(s.total_purchased)}</td>
                  <td style={tdStyle}><Badge value={s.status} /></td>
                  <td style={tdStyle}>
                    <button style={{ ...btnSmall, marginRight: 6 }} onClick={() => setEditing(s)}>Edit</button>
                    <button style={{ ...btnSmall, color: '#ef4444' }} onClick={() => removeItem(s.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <SupplierModal companyId={companyId} supplier={editing}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); fetchList(); }} />
      )}
      {viewing && <SupplierDetail companyId={companyId} supplier={viewing} onClose={() => setViewing(null)} onChanged={() => { viewSupplier(viewing.id); fetchList(); }} />}
    </div>
  );
}

function SupplierModal({ companyId, supplier, onClose, onSaved }) {
  const isNew = !supplier.id;
  const [quick, setQuick] = useState(isNew);
  const [form, setForm] = useState({
    name: supplier.name || '', legal_name: supplier.legal_name || '', supplier_type: supplier.supplier_type || '',
    contact_person: supplier.contact_person || '', phone: supplier.phone || '', mobile: supplier.mobile || '',
    email: supplier.email || '', website: supplier.website || '',
    address: supplier.address || '', city: supplier.city || '', country: supplier.country || '',
    tax_number: supplier.tax_number || '', vat_number: supplier.vat_number || '',
    payment_terms: supplier.payment_terms || '', default_currency: supplier.default_currency || 'USD',
    notes: supplier.notes || '', status: supplier.status || 'active',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/companies/${companyId}/procurement/suppliers` : `${API}/companies/${companyId}/procurement/suppliers/${supplier.id}`;
      const body = isNew ? { ...form, quick_create: quick } : form;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'New Supplier' : 'Edit Supplier'}</h3>
        {isNew && (
          <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <input type="checkbox" checked={quick} onChange={e => setQuick(e.target.checked)} /> Quick Create (Name only — supplier stays Incomplete)
          </label>
        )}
        <FormRow label="Name *"><input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormRow>
        {!quick && (
          <>
            <FormRow label="Legal Name"><input style={inputStyle} value={form.legal_name} onChange={e => setForm({ ...form, legal_name: e.target.value })} /></FormRow>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="Contact Person"><input style={inputStyle} value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="Phone"><input style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></FormRow></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="Email"><input style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="Website"><input style={inputStyle} value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></FormRow></div>
            </div>
            <FormRow label="Address"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></FormRow>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="City"><input style={inputStyle} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="Country"><input style={inputStyle} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></FormRow></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="Tax Number"><input style={inputStyle} value={form.tax_number} onChange={e => setForm({ ...form, tax_number: e.target.value })} /></FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="VAT Number"><input style={inputStyle} value={form.vat_number} onChange={e => setForm({ ...form, vat_number: e.target.value })} /></FormRow></div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}><FormRow label="Payment Terms"><input style={inputStyle} value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="net_30" /></FormRow></div>
              <div style={{ flex: 1 }}><FormRow label="Default Currency"><input style={inputStyle} value={form.default_currency} onChange={e => setForm({ ...form, default_currency: e.target.value })} /></FormRow></div>
            </div>
            <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
          </>
        )}
        {!isNew && (
          <FormRow label="Status">
            <select style={inputStyle} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="incomplete">Incomplete</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </FormRow>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function SupplierDetail({ companyId, supplier, onClose, onChanged }) {
  const [rating, setRating] = useState('');
  const [comment, setComment] = useState('');

  const submitRating = async () => {
    if (!rating) return;
    await fetch(`${API}/companies/${companyId}/procurement/suppliers/${supplier.id}/rate`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ rating: Number(rating), comment }),
    });
    setRating(''); setComment('');
    onChanged();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{supplier.name} <Badge value={supplier.status} /></h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
          <div><b>Code:</b> {supplier.supplier_code}</div>
          <div><b>Rating:</b> {supplier.rating || '—'} / 5</div>
          <div><b>Contact:</b> {supplier.contact_person || '—'}</div>
          <div><b>Phone:</b> {supplier.phone || '—'}</div>
          <div><b>Email:</b> {supplier.email || '—'}</div>
          <div><b>Tax #:</b> {supplier.tax_number || '—'}</div>
          <div><b>Total Purchased:</b> {fmtMoney(supplier.total_purchased)}</div>
          <div><b>PO Count:</b> {supplier.po_count || 0}</div>
        </div>

        <h4>Rate Supplier</h4>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <select style={{ ...inputStyle, width: 90 }} value={rating} onChange={e => setRating(e.target.value)}>
            <option value="">—</option>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <input style={inputStyle} placeholder="Comment (optional)" value={comment} onChange={e => setComment(e.target.value)} />
          <button style={btnSecondary} onClick={submitRating}>Submit</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
